import React from "react";

import { Controller, useForm } from "react-hook-form";

import { useUpdateSdkBundle } from "src/api/connectApi/queries";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { getSdkBundleErrorDescription } from "src/connections/sdk/bundleErrors";
import { SdkScriptSelect } from "src/connections/sdk/SdkScriptSelect";
import { BUNDLE_RULE_DESCRIPTION } from "src/connections/sdk/scriptOptions";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type BundleFormValues = {
  name: string;
  configIds: string[];
};

type Props = {
  open: boolean;
  bundle?: ProviderSdkBundleResponse;
  configs: ProviderSdkConfigResponse[];
  baseUrl: string | undefined;
  onClose: () => void;
  afterLeave?: () => void;
};

const buildBundleUrl = (
  baseUrl: string | undefined,
  bundle: ProviderSdkBundleResponse,
) =>
  `https://${baseUrl ?? ""}/sdk/api/v1/bundles/${encodeURIComponent(
    bundle.slug,
  )}/init`;

export const SdkBundleModal: React.FC<Props> = ({
  open,
  bundle,
  configs,
  baseUrl,
  onClose,
  afterLeave,
}) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BundleFormValues>({
    values: {
      name: bundle?.name ?? "",
      configIds: bundle?.configs.map((config) => config.id) ?? [],
    },
  });

  const updateBundle = useUpdateSdkBundle(baseUrl);

  const onSubmit = async (values: BundleFormValues) => {
    if (!bundle) return;

    try {
      await updateBundle.mutateAsync({
        id: bundle.id,
        payload: { name: values.name, config_ids: values.configIds },
      });
      toastActions.success({
        title: "SDK Bundle updated",
        description: `Bundle ${values.name} was updated.`,
      });
      onClose();
    } catch (error) {
      toastActions.failure({
        title: "Failed to update SDK Bundle",
        description: getSdkBundleErrorDescription(
          error,
          `Bundle ${values.name} could not be saved. Please try again.`,
        ),
      });
    }
  };

  return (
    <Modal afterLeave={afterLeave} open={open} onClose={onClose}>
      <Modal.Header>
        {bundle ? `Edit ${bundle.name}` : "Edit SDK Bundle"}
      </Modal.Header>
      <form data-loc="sdk-bundle-form" onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <FormItem gap="sm" label="Name" isRequired>
            {Boolean(errors.name) && (
              <ErrorHint>{errors.name?.message}</ErrorHint>
            )}
            <Input
              data-loc="sdk-bundle-name"
              placeholder="e.g. Strict device intelligence"
              {...register("name", { required: "Name is required" })}
              fullWidth
            />
          </FormItem>

          {bundle && (
            <FormItem
              description="Use this URL to load the bundle configuration for the Web SDK."
              gap="sm"
              label="Bundle URL"
            >
              <div className="flex items-center gap-2">
                <Input
                  data-loc="sdk-bundle-url"
                  value={buildBundleUrl(baseUrl, bundle)}
                  disabled
                  fullWidth
                  readOnly
                />
                <CopyTextIcon
                  dataLoc="copy-sdk-bundle-url"
                  feedback="toast"
                  value={buildBundleUrl(baseUrl, bundle)}
                />
              </div>
            </FormItem>
          )}

          <FormItem
            description={BUNDLE_RULE_DESCRIPTION}
            gap="sm"
            label="Select SDK Scripts"
            isRequired
          >
            {Boolean(errors.configIds) && (
              <ErrorHint>{errors.configIds?.message}</ErrorHint>
            )}
            <Controller
              control={control}
              name="configIds"
              render={({ field: { value, onChange } }) => (
                <SdkScriptSelect
                  configs={configs}
                  dataLoc="sdk-bundle-scripts"
                  value={value}
                  onChange={onChange}
                />
              )}
              rules={{
                validate: (value) =>
                  value.length > 0 || "Select at least one SDK Script",
              }}
            />
          </FormItem>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="sdk-bundle-save"
              isDisabled={isSubmitting}
              htmlType="submit"
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
